/** @jsxImportSource @emotion/react */
import React from "react";
import { css, Interpolation, Theme } from "@emotion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { useSurfaceRipple } from "components/hooks/useSurfaceRipple";
import Colors from "styles/colors";

// Utility to check if a value is a valid React element (not null, undefined, or empty string)
function isValidIcon(icon: React.ReactNode) {
  return icon !== null && icon !== undefined && icon !== "";
}

interface ChipProps {
  label: React.ReactNode;
  size?: "small" | "medium";
  variant?: "filled" | "outline";
  className?: string;
  css?: Interpolation<Theme>;
  onClick?: (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
  onStartIconClick?: () => void;
  startIcon?: React.ReactNode;
  onEndIconClick?: () => void;
  endIcon?: React.ReactNode;
  onDelete?: () => void;
  disabled?: boolean;
  disableRipple?: boolean;
}

const sizeStyles = {
  small: {
    height: 24,
    fontSize: 12,
    borderRadius: 12,
  },
  medium: {
    height: 32,
    fontSize: 14,
    borderRadius: 16,
  },
};

const getChipStyle = (
  variant: "filled" | "outline",
  clickable: boolean,
  size: "small" | "medium",
  disabled: boolean = false,
) =>
  css({
    display: "inline-flex",
    position: "relative",
    alignItems: "center",
    justifyContent: "center",
    boxAlign: "center",
    ...sizeStyles[size],
    background:
      variant === "filled" ? Colors.background.LIGHT_GRAY : "transparent",
    color: Colors.text.BLUE_BLACK,
    border: variant === "outline" ? `1px solid #e0e0e0` : "none",
    userSelect: "none",
    cursor: disabled ? "not-allowed" : clickable ? "pointer" : "default",
    opacity: disabled ? 0.5 : 1,
    pointerEvents: disabled ? "none" : undefined,
    transition:
      "background 0.2s, border 0.2s, color 0.2s, box-shadow 0.18s cubic-bezier(0.4,0,0.2,1)",
    boxShadow: "none",
    ":hover":
      !disabled && clickable
        ? {
            background:
              variant === "filled"
                ? "rgba(0, 0, 0, 0.12)"
                : "rgba(0, 0, 0, 0.06)",
          }
        : undefined,
    ":active":
      !disabled && clickable
        ? {
            boxShadow: "0 1px 0 0 rgba(0,0,0,0.25)",
          }
        : undefined,
  });

const getStartIconStyle = (
  size: "small" | "medium" = "medium",
  clickable = true,
  disabled = false,
) =>
  css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: size === "small" ? "0px -4px 0px 3px" : "0px -6px 0px 5px",
    cursor: disabled ? "not-allowed" : clickable ? "pointer" : "default",
    opacity: disabled ? 0.5 : 1,
    pointerEvents: disabled ? "none" : undefined,
    alignSelf: "center",
    fontSize: size === "small" ? 16 : 22,
    color: "rgba(0, 0, 0, 0.26)",
    borderRadius: "50%",
    transition:
      clickable && !disabled ? "color 0.2s, background 0.2s" : undefined,
    outline: "none",
    ...(clickable && !disabled
      ? {
          ":hover": { color: "rgba(0, 0, 0, 0.35)" },
        }
      : {}),
    ":focus": {
      outline: "none",
      boxShadow: "none",
    },
  });

const getEndIconStyle = (
  size: "small" | "medium" = "medium",
  clickable = true,
  disabled = false,
) =>
  css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: size === "small" ? "0px 3px 0px -4px" : "0px 5px 0px -6px",
    cursor: disabled ? "not-allowed" : clickable ? "pointer" : "default",
    opacity: disabled ? 0.5 : 1,
    pointerEvents: disabled ? "none" : undefined,
    alignSelf: "center",
    fontSize: size === "small" ? 16 : 22,
    color: "rgba(0, 0, 0, 0.26)",
    borderRadius: "50%",
    transition:
      clickable && !disabled ? "color 0.2s, background 0.2s" : undefined,
    outline: "none",
    ...(clickable && !disabled
      ? {
          ":hover": { color: "rgba(0, 0, 0, 0.35)" },
        }
      : {}),
    ":focus": {
      outline: "none",
      boxShadow: "none",
    },
  });

const labelStyle = (size: "small" | "medium") =>
  css({
    display: "flex",
    alignItems: "center",
    height: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    padding: size === "small" ? "0 8px" : "0 12px",
    marginTop: "1px",
  });

export const Chip: React.FC<ChipProps> = ({
  label,
  size = "medium",
  variant = "filled",
  className,
  css: cssProp,
  onClick,
  onStartIconClick,
  startIcon,
  onEndIconClick,
  endIcon,
  onDelete,
  disabled = false,
  disableRipple = false,
}) => {
  const clickable = typeof onClick === "function";
  const showDelete = !!onDelete && !onEndIconClick && !endIcon;
  const ripple = useSurfaceRipple();

  return (
    <span
      ref={!disableRipple && ripple ? ripple.rippleContainerRef : undefined}
      css={[
        getChipStyle(variant, clickable, size, disabled),
        cssProp,
        { position: "relative", overflow: "hidden" },
      ]}
      {...(className ? { className } : {})}
      onClick={
        !disabled && clickable
          ? (e) => {
              if (!disableRipple && ripple) {
                ripple.createRipple(e);
              }
              onClick(e);
            }
          : undefined
      }
      tabIndex={!disabled && clickable ? 0 : -1}
      role={!disabled && clickable ? "button" : undefined}
      aria-disabled={disabled || undefined}
    >
      {!disableRipple && ripple && <span css={ripple.rippleStyles} />}
      {isValidIcon(startIcon) &&
        (onStartIconClick && !disabled ? (
          <span
            css={getStartIconStyle(size, true, false)}
            onClick={(e) => {
              e.stopPropagation();
              onStartIconClick();
            }}
            role="button"
            aria-label="Start Icon"
            tabIndex={0}
          >
            {startIcon}
          </span>
        ) : (
          <span css={getStartIconStyle(size, false, disabled)}>
            {startIcon}
          </span>
        ))}
      <span css={labelStyle(size)}>{label}</span>
      {isValidIcon(endIcon) &&
        (onEndIconClick && !disabled ? (
          <span
            css={getEndIconStyle(size, true, false)}
            onClick={(e) => {
              e.stopPropagation();
              onEndIconClick();
            }}
            role="button"
            aria-label="End Icon"
            tabIndex={0}
          >
            {endIcon}
          </span>
        ) : (
          <span css={getEndIconStyle(size, false, disabled)}>{endIcon}</span>
        ))}
      {!disabled && showDelete && (
        <FontAwesomeIcon
          icon={faTimesCircle}
          css={getEndIconStyle(size, true, false)}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          role="button"
          aria-label="Delete"
          tabIndex={0}
        />
      )}
    </span>
  );
};

export default Chip;
